/** Hooks for group management: listing, creating, joining, leaving, and member queries. */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { captureSentryException } from '@/lib/sentry';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { mapSupabaseError } from '@/lib/errorMapper';
import { assertOnline } from '@/lib/networkGuard';
import { toast } from 'sonner';
import posthog from '@/lib/posthog';

export interface Group {
  id: string;
  name: string;
  description: string | null;
  invite_code: string;
  created_by: string;
  created_at: string;
}

export type GroupWithMembership = Group;

export interface GroupMember {
  id: string;
  role: string;
  user_id: string;
  created_at: string;
  display_name: string | null;
  avatar_url: string | null;
}

/**
 * Returns all groups the current user is a member of.
 *
 * Queries via `group_memberships` rather than `groups` directly so only
 * groups the user belongs to are returned (RLS is also enforced server-side).
 *
 * @returns A TanStack `UseQueryResult` resolving to an array of {@link GroupWithMembership} objects.
 */
export function useGroups() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['groups', user?.id],
    queryFn: async (): Promise<GroupWithMembership[]> => {
      // Query via group_memberships rather than groups directly so we only
      // see groups this user actually belongs to (RLS enforced server-side too).
      const { data, error } = await supabase
        .from('group_memberships')
        .select(
          `
          groups (
            id,
            name,
            description,
            invite_code,
            created_by,
            created_at
          )
        `,
        )
        .eq('user_id', user!.id);

      if (error) throw error;

      return data.map((m) => ({
        ...(m.groups as unknown as Group),
      }));
    },
    enabled: !!user,
  });
}

/**
 * Creates a new group owned by the current user.
 *
 * The DB enforces tier-based group limits (free: 1, paid: unlimited) via the
 * `check_group_creation_limit` trigger.
 *
 * @returns A TanStack `UseMutationResult`. The mutation accepts `{ name: string; description?: string }`.
 * @throws Supabase error if the tier group limit is reached.
 */
export function useCreateGroup() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ name, description }: { name: string; description?: string }) => {
      await assertOnline();
      const { data, error } = await supabase
        .from('groups')
        .insert({
          name,
          description,
          created_by: user!.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      posthog.capture('group_created', { group_id: data.id });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}

/**
 * Joins a group via invite code using the `join_group_with_code` RPC.
 *
 * The RPC atomically validates the code and creates the membership in a
 * single transaction, enforcing:
 *   - The invite code exists and matches an active (non-revoked, non-expired) invite.
 *   - The caller is not already a member of the group.
 *   - The group has not reached the 50-member hard cap.
 *   - The caller's tier-based group limit has not been exceeded.
 *
 * @returns A TanStack `UseMutationResult`. The mutation accepts the invite code string.
 * @throws Supabase error if validation fails (duplicate member, expired code, cap reached).
 */
export function useJoinGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inviteCode: string) => {
      await assertOnline();
      const { data, error } = await supabase.rpc('join_group_with_code', {
        p_invite_code: inviteCode,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      posthog.capture('group_joined', { group_id: data });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}

/**
 * Fetches a single group by ID for use in group-detail pages.
 *
 * Unlike {@link useGroups}, this queries the `groups` table directly rather
 * than going through `group_memberships`. Access control is still enforced
 * server-side by the RLS policy on `groups`. Use this hook when you already
 * know the group ID and only need the group metadata.
 *
 * @param groupId - The UUID of the group to fetch.
 * @returns A TanStack `UseQueryResult` resolving to a {@link Group} object.
 */
export function useGroup(groupId: string) {
  return useQuery({
    queryKey: ['group', groupId],
    queryFn: async () => {
      const { data, error } = await supabase.from('groups').select('*').eq('id', groupId).single();

      if (error) throw error;
      return data as Group;
    },
    enabled: !!groupId,
  });
}

/**
 * Returns all members of a group with their display names and avatars.
 *
 * Profiles are fetched in a separate query rather than a join because the
 * `profiles` table is not directly joinable via PostgREST on this schema.
 *
 * @param groupId - The UUID of the group whose members to fetch.
 * @returns A TanStack `UseQueryResult` resolving to an array of {@link GroupMember} objects.
 */
export function useGroupMembers(groupId: string) {
  return useQuery({
    queryKey: ['group-members', groupId],
    queryFn: async (): Promise<GroupMember[]> => {
      const { data: memberships, error } = await supabase
        .from('group_memberships')
        .select('id, role, user_id, created_at')
        .eq('group_id', groupId);

      if (error) throw error;
      if (!memberships || memberships.length === 0) return [];

      // Fetch profile display names separately — the profiles table is not
      // directly joinable via PostgREST on this schema setup.
      const userIds = memberships.map((m) => m.user_id);
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, display_name, avatar_url')
        .in('user_id', userIds);

      if (profilesError) throw profilesError;

      const profileMap = new Map(profiles?.map((p) => [p.user_id, p]) || []);

      return memberships.map((m) => ({
        ...m,
        display_name: profileMap.get(m.user_id)?.display_name || null,
        avatar_url: profileMap.get(m.user_id)?.avatar_url || null,
      }));
    },
    enabled: !!groupId,
  });
}

export interface ActiveLoanInfo {
  loan_id: string;
  item_id: string;
  item_name: string;
  borrower_id: string;
  owner_id: string;
  borrowed_at: string;
  role: 'owner' | 'borrower';
}

/**
 * Imperatively checks whether a group has active loans involving the current user.
 *
 * Uses `useMutation` rather than `useQuery` because the check is triggered
 * on demand (e.g. before confirming a "leave group" action), not on mount.
 *
 * @returns A TanStack `UseMutationResult`. The mutation accepts the group ID string
 *   and resolves to an array of {@link ActiveLoanInfo} describing any blocking loans.
 */
export function useCheckActiveLoans() {
  return useMutation({
    mutationFn: async (groupId: string): Promise<ActiveLoanInfo[]> => {
      const { data, error } = await supabase.rpc('check_active_loans_in_group', {
        p_group_id: groupId,
      });

      if (error) throw error;
      return (data as unknown as ActiveLoanInfo[]) || [];
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}

/**
 * Removes the current user from a group via the `leave_group` RPC.
 *
 * The RPC rejects the request if the user has active loans (as owner or
 * borrower) that must be resolved first. On success, group, item, and
 * member query caches are invalidated since the user loses access to the
 * group's shared items.
 *
 * @returns A TanStack `UseMutationResult`. The mutation accepts the group ID string.
 * @throws Supabase error if the user has unresolved active loans in the group.
 */
export function useLeaveGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupId: string) => {
      await assertOnline();
      const { error } = await supabase.rpc('leave_group', {
        p_group_id: groupId,
      });

      if (error) throw error;
    },
    onSuccess: (_, groupId) => {
      // Invalidate group list, items, and member list since leaving
      // removes access to the group's shared items.
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      queryClient.invalidateQueries({ queryKey: ['group-members'] });
      queryClient.invalidateQueries({ queryKey: ['loan-requests'] });
      posthog.capture('group_left', { group_id: groupId });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}
